<template>
  <div class="description-wrapper">
    <tiptap-vuetify
      label="Tell us about your business"
      placeholder="Description"
      class="my-custom-editor"
      :extensions="extensions"
      v-model="internalDescription"
      required
    ></tiptap-vuetify>

    <small class="my-3 align-right">Word count: ({{ this.wordCount }})</small>

    <div v-if="description">
      <small class="my-3 red--text" v-if="!descriptionIsValid">
        Description must be a minium of ({{ this.minDesc }}) and maximum of ({{
          this.maxDesc
        }})
      </small>
    </div>
  </div>
</template>

<script>
import {
  TiptapVuetify,
  Heading,
  Bold,
  Italic,
  Strike,
  Underline,
  Code,
  Paragraph,
  BulletList,
  OrderedList,
  ListItem,
  Link,
  Blockquote,
  HardBreak,
  HorizontalRule,
  History
} from 'tiptap-vuetify'

export default {
  components: {
    TiptapVuetify
  },
  props: ['description'],
  data() {
    return {
      minDesc: 200,
      maxDesc: 5000,
      wordCount: 0,
      internalDescription: this.description,
      extensions: [
        History,
        Blockquote,
        Link,
        Underline,
        Strike,
        Italic,
        ListItem,
        BulletList,
        OrderedList,
        [
          Heading,
          {
            options: {
              levels: [1, 2, 3, 4]
            }
          }
        ],
        Bold,
        Code,
        HorizontalRule,
        Paragraph,
        HardBreak
      ]
    }
  },
  computed: {
    descriptionIsValid() {
      return this.validateDescription()
    }
  },
  methods: {
    validateDescription() {
      // Add your description validation logic here
      // Extract text content from HTML
      const tempElement = document.createElement('div.tiptap-vuetify-editor')
      tempElement.innerHTML = this.description
      const innerText = tempElement.textContent || tempElement.innerText
      const textCount = innerText.length
      this.wordCount = textCount
      // Use innerText for validation
      const validate =
        textCount > this.maxDesc || textCount < this.minDesc ? false : true

      this.$emit('validate', validate)
      return validate
    }
  },
  watch: {
    internalDescription(newVal) {
      // Emit the input event to update the description in the parent
      this.$emit('input', newVal)
      this.validateDescription(newVal)
    },
    description(newVal) {
      // Update the internal description if the parent changes the prop
      this.internalDescription = newVal
      // check validation
    }
  }
}
</script>

<style scoped>
/* Wrapper to remove any padding */
.description-wrapper {
  width: 100%;
  margin: 0 !important;
  padding: 0 !important;
  background-color: transparent !important;
}

/* Main editor container - remove black background and padding */
.my-custom-editor {
  width: 100%;
  background-color: transparent !important;
  margin: 0 !important;
  padding: 0 !important;
}

/* Remove any wrapper divs that might have black backgrounds */
.my-custom-editor ::v-deep > * {
  background-color: transparent !important;
}

/* Ensure the root tiptap container has no padding or black background */
.my-custom-editor ::v-deep .v-card,
.my-custom-editor ::v-deep .v-sheet {
  background-color: transparent !important;
  padding: 0 !important;
  margin: 0 !important;
}

/* Remove black background from all tiptap wrapper elements */
.my-custom-editor ::v-deep .tiptap-vuetify-editor {
  background-color: transparent !important;
  border: none !important;
  padding: 0 !important;
  margin: 0 !important;
}

.my-custom-editor ::v-deep .tiptap-vuetify-editor__content {
  background-color: transparent !important;
  padding: 0 !important;
  margin: 0 !important;
}

.my-custom-editor ::v-deep .v-input {
  background-color: transparent !important;
  padding: 0 !important;
  margin: 0 !important;
}

.my-custom-editor ::v-deep .v-input__control {
  background-color: transparent !important;
  padding: 0 !important;
  margin: 0 !important;
}

/* Editor wrapper - white background matching form fields */
.my-custom-editor ::v-deep .v-input__slot {
  background-color: #ffffff !important;
  border: 1px solid rgba(0, 0, 0, 0.38) !important;
  border-radius: 4px !important;
  padding: 0 !important;
  margin: 0 !important;
  box-shadow: none !important;
}

/* Toolbar styling - light gray background matching form theme */
.my-custom-editor ::v-deep .v-toolbar {
  background-color: #f5f5f5 !important;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  border-top: 1px solid rgba(0, 0, 0, 0.38);
  border-left: 1px solid rgba(0, 0, 0, 0.38);
  border-right: 1px solid rgba(0, 0, 0, 0.38);
  border-radius: 4px 4px 0 0;
  margin: 0 !important;
  padding: 0 !important;
}

.my-custom-editor ::v-deep .v-toolbar__content {
  padding: 8px 12px;
}

/* Editor content area - white background matching form field */
.my-custom-editor ::v-deep .ProseMirror {
  background-color: #ffffff !important;
  color: rgba(0, 0, 0, 0.87) !important;
  min-height: 200px;
  padding: 12px 16px;
  border-top: none !important;
  border-left: 1px solid rgba(0, 0, 0, 0.38) !important;
  border-right: 1px solid rgba(0, 0, 0, 0.38) !important;
  border-bottom: 1px solid rgba(0, 0, 0, 0.38) !important;
  border-radius: 0 0 4px 4px !important;
  transition: border-color 0.3s;
  width: 100%;
  box-sizing: border-box;
  margin: 0 !important;
}

/* Remove any black backgrounds from tiptap wrapper elements */
.my-custom-editor ::v-deep .tiptap-vuetify-editor-wrapper,
.my-custom-editor ::v-deep .tiptap-vuetify-editor__wrapper {
  background-color: transparent !important;
  padding: 0 !important;
  margin: 0 !important;
}

.my-custom-editor ::v-deep .ProseMirror:focus {
  border-color: #1976d2 !important;
  border-width: 2px;
  outline: none;
}

/* Placeholder text styling */
.my-custom-editor ::v-deep .ProseMirror p.is-editor-empty:first-child::before {
  color: rgba(0, 0, 0, 0.38) !important;
}

/* Word count and validation text */
small {
  display: block;
  text-align: right;
  font-size: 12px;
  padding: 4px 8px;
  margin-top: 4px;
}

/* Mobile responsive styles for tiptap editor */
@media (max-width: 600px) {
  .my-custom-editor {
    width: 100%;
  }

  /* Ensure the editor container is responsive */
  .my-custom-editor ::v-deep .tiptap-vuetify-editor {
    width: 100%;
    min-width: 0;
  }

  /* Make the toolbar responsive */
  .my-custom-editor ::v-deep .v-toolbar {
    flex-wrap: wrap;
    min-height: auto;
  }

  .my-custom-editor ::v-deep .v-toolbar__content {
    flex-wrap: wrap;
    padding: 4px 8px;
  }

  /* Adjust button sizes on mobile */
  .my-custom-editor ::v-deep .v-btn {
    min-width: 36px;
    padding: 0 8px;
    height: 36px;
  }

  .my-custom-editor ::v-deep .v-btn .v-icon {
    font-size: 18px;
  }

  /* Ensure editor content area is responsive */
  .my-custom-editor ::v-deep .ProseMirror {
    width: 100%;
    min-width: 0;
    padding: 12px;
    font-size: 16px; /* Prevents zoom on iOS */
    min-height: 150px;
  }

  /* Make the input field responsive */
  .my-custom-editor ::v-deep .v-input {
    width: 100%;
  }

  .my-custom-editor ::v-deep .v-input__control {
    width: 100%;
  }

  /* Word count and validation text */
  small {
    display: block;
    text-align: right;
    font-size: 12px;
    padding: 4px 8px;
  }
}
</style>
